import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, forkJoin } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { PackageCardsComponent } from '../package-cards/package-cards.component';
import { PrepaidWashBooksTableComponent } from './prepaid-wash-books-table/prepaid-wash-books-table.component';
import {
  PaginationBarComponent,
  PaginationData,
} from '../../shared/components/pagination-bar/pagination-bar.component';
import {
  LoadingTableShimmerComponent,
  ShimmerColumn,
} from '../../shared/components/loading-table-shimmer/loading-table-shimmer.component';
import { PrepaidWashBookService } from '../../services/prepaid-wash-book.service';
import {
  DEFAULT_PREPAID_WASH_BOOKS_PAGE_LIMIT,
  PrepaidWashBook,
  sumPrepaidBooksRedeemed,
} from '../../models/prepaid-wash-book.models';

@Component({
  selector: 'app-prepaid-wash-books',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PackageCardsComponent,
    PrepaidWashBooksTableComponent,
    PaginationBarComponent,
    LoadingTableShimmerComponent,
  ],
  templateUrl: './prepaid-wash-books.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './prepaid-wash-books.component.scss',
})
export class PrepaidWashBooksComponent implements OnInit, OnDestroy {
  @Output() edit = new EventEmitter<PrepaidWashBook>();

  private readonly prepaidWashBookService = inject(PrepaidWashBookService);
  private readonly destroy$ = new Subject<void>();
  private readonly searchInput$ = new Subject<string>();

  books: PrepaidWashBook[] = [];
  loading = false;
  searchTerm = '';
  statusFilter: 'all' | 'active' | 'inactive' = 'all';

  totalCount = 0;
  activeCount = 0;
  inactiveCount = 0;
  redeemedCount = 0;

  currentPage = 1;
  itemsPerPage = DEFAULT_PREPAID_WASH_BOOKS_PAGE_LIMIT;
  totalItems = 0;
  totalPages = 1;

  readonly packageImage = 'assets/package-3d/p-1.png';
  readonly packageImage2 = 'assets/package-3d/p-2.png';
  readonly packageImage3 = 'assets/package-3d/p-3.png';
  readonly redeemedImage = 'assets/package-3d/p-2.png';

  readonly shimmerColumns: ShimmerColumn[] = [
    { label: 'ID', width: 'sm' },
    { label: 'Prepaid Package', width: 'lg' },
    { label: 'Wash Configs', width: 'md' },
    { label: 'Status', width: 'sm' },
    { label: 'List Price', width: 'sm' },
    { label: 'Total Orders', width: 'sm' },
    { label: 'Pending Redemptions', width: 'md' },
    { label: 'Last Updated', width: 'sm' },
    { label: 'Actions', width: 'sm' },
  ];

  ngOnInit(): void {
    this.searchInput$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.currentPage = 1;
        this.loadBooks();
      });
    this.loadStats();
    this.loadBooks();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Called from parent after a prepaid book is created. */
  refresh(): void {
    this.loadStats();
    this.loadBooks();
  }

  onBookDeleted(): void {
    if (this.books.length === 1 && this.currentPage > 1) {
      this.currentPage -= 1;
    }
    this.refresh();
  }

  onEditBook(book: PrepaidWashBook): void {
    this.edit.emit(book);
  }

  get paginationData(): PaginationData {
    return {
      currentPage: this.currentPage,
      totalPages: this.totalPages,
      totalItems: this.totalItems,
      itemsPerPage: this.itemsPerPage,
    };
  }

  get totalCountDisplay(): string {
    return String(this.totalCount);
  }

  get activeCountDisplay(): string {
    return String(this.activeCount);
  }

  get inactiveCountDisplay(): string {
    return String(this.inactiveCount);
  }

  get redeemedCountDisplay(): string {
    return String(this.redeemedCount);
  }

  onSearchChange(term: string): void {
    this.searchTerm = term;
    this.searchInput$.next(term);
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.searchInput$.next('');
  }

  onStatusFilterChange(value: 'all' | 'active' | 'inactive'): void {
    if (this.statusFilter === value) {
      return;
    }
    this.statusFilter = value;
    this.currentPage = 1;
    this.loadBooks();
  }

  onPageChange(page: number): void {
    if (page === this.currentPage) {
      return;
    }
    this.currentPage = page;
    this.loadBooks();
  }

  onItemsPerPageChange(limit: number): void {
    if (limit === this.itemsPerPage) {
      return;
    }
    this.itemsPerPage = limit;
    this.currentPage = 1;
    this.loadBooks();
  }

  trackByBookId(_: number, book: PrepaidWashBook): string {
    return book.id;
  }

  private loadStats(): void {
    forkJoin({
      all: this.prepaidWashBookService.getPrepaidWashBooks({
        page: 1,
        limit: 1,
      }),
      active: this.prepaidWashBookService.getPrepaidWashBooks({
        page: 1,
        limit: 1,
        status: true,
      }),
      inactive: this.prepaidWashBookService.getPrepaidWashBooks({
        page: 1,
        limit: 1,
        status: false,
      }),
      redeemedSample: this.prepaidWashBookService.getPrepaidWashBooks({
        page: 1,
        limit: 100,
      }),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ all, active, inactive, redeemedSample }) => {
          this.totalCount = all.pagination.total;
          this.activeCount = active.pagination.total;
          this.inactiveCount = inactive.pagination.total;
          this.redeemedCount = sumPrepaidBooksRedeemed(redeemedSample.books);
        },
        error: () => {
          this.totalCount = 0;
          this.activeCount = 0;
          this.inactiveCount = 0;
          this.redeemedCount = 0;
        },
      });
  }

  private loadBooks(): void {
    this.loading = true;
    const params: {
      page: number;
      limit: number;
      status?: boolean;
      search?: string;
    } = {
      page: this.currentPage,
      limit: this.itemsPerPage,
    };
    if (this.statusFilter === 'active') {
      params.status = true;
    } else if (this.statusFilter === 'inactive') {
      params.status = false;
    }
    const search = this.searchTerm.trim();
    if (search) {
      params.search = search;
    }

    this.prepaidWashBookService
      .getPrepaidWashBooks(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ books, pagination }) => {
          this.books = books;
          this.currentPage = pagination.page;
          this.itemsPerPage = pagination.limit;
          this.totalItems = pagination.total;
          this.totalPages = Math.max(1, pagination.total_pages);
          this.loading = false;
        },
        error: () => {
          this.books = [];
          this.totalItems = 0;
          this.totalPages = 1;
          this.loading = false;
        },
      });
  }
}
